class LoadMoreProducts extends HTMLElement {
  constructor() {
    super();

    this.handleFacetUpdate = this.handleFacetUpdate.bind(this);
    this.loadMoreProducts = this.loadMoreProducts.bind(this);
    this.handleDocumentClick = this.handleDocumentClick.bind(this);

    this.isLoading = false;
    this.isInitialized = false;
    this.currentPage = 1;
    this.totalPages = 1;
    this.totalProducts = 0;
    this.loadedProducts = 0;
    this.pageSize = 16;

    this.productGrid = null;
    this.paginationInfo = null;
    this.loadMoreBtn = null;
    this.productsCountDisplay = null;

    this.progressContainer = null;
    this.progressBar = null;

    this.paginationContainer = null;
  }

  connectedCallback() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }
  }

  disconnectedCallback() {
    this.cleanup();
  }

  init() {
    if (this.isInitialized) return;

    try {
      this.setupElements();
      this.setupEventListeners();
      this.updateState();
      this.isInitialized = true;
    } catch (error) {
      console.error('InfiniteScroll: Initialization failed', error);
    }
  }

  setupElements() {
    const productGridSelector = this.dataset.productGrid || '#product-grid';
    const paginationInfoSelector = this.dataset.paginationInfo || '#pagination-info';
    const loadMoreBtnSelector = this.dataset.loadMoreBtn || '.load-more-btn';
    const productsCountDisplaySelector = this.dataset.productsCountDisplay || '#products-count-display';

    this.productGrid = document.querySelector(productGridSelector);
    this.paginationInfo = document.querySelector(paginationInfoSelector);
    this.loadMoreBtn = document.querySelector(loadMoreBtnSelector);
    this.productsCountDisplay = document.querySelector(productsCountDisplaySelector);

    this.progressContainer = document.querySelector('.inner__progress-container') || null;
    this.progressBar = document.querySelector('.inner__progress-bar') || null;

    this.paginationContainer = document.querySelector('.pagination__btn-container') || null;

    if (!this.productGrid) {
      throw new Error(`Product grid not found: ${productGridSelector}`);
    }

    const pageSizeAttr = parseInt(this.productGrid.dataset.pageSize);
    if (!Number.isNaN(pageSizeAttr) && pageSizeAttr > 0) {
      this.pageSize = pageSizeAttr;
    } else if (this.productGrid.dataset.paginatePageSize) {
      const alt = parseInt(this.productGrid.dataset.paginatePageSize);
      if (!Number.isNaN(alt) && alt > 0) this.pageSize = alt;
    }
  }

  setupEventListeners() {
    this.cleanup();

    this.loadMoreBtn = document.querySelector('.load-more-btn');

    if (this.loadMoreBtn) {
      this.loadMoreBtn.addEventListener('click', this.loadMoreProducts);
      console.log('LoadMoreProducts: Button event listener attached');
    } else {
      console.log('LoadMoreProducts: Button not found');
    }

    document.addEventListener('click', this.handleDocumentClick);
    document.addEventListener('facet:update', this.handleFacetUpdate);

    console.log('LoadMoreProducts: Event listeners attached');
  }

  handleDocumentClick(event) {
    if (event.target && event.target.classList.contains('load-more-btn')) {
      event.preventDefault();
      if (!this.isLoading) {
        this.loadMoreProducts();
      }
    }
  }

  cleanup() {
    if (this.loadMoreBtn) {
      this.loadMoreBtn.removeEventListener('click', this.loadMoreProducts);
    }

    document.removeEventListener('click', this.handleDocumentClick);
    document.removeEventListener('facet:update', this.handleFacetUpdate);
  }

  updateState() {
    if (!this.productGrid) return;

    this.currentPage = parseInt(this.productGrid.dataset.currentPage) || this.currentPage || 1;
    this.totalPages = parseInt(this.productGrid.dataset.totalPages) || this.totalPages || 1;
    this.totalProducts = parseInt(this.productGrid.dataset.productsCount) || this.totalProducts || 0;
    const productNodes = this.productGrid.querySelectorAll('.custom-grid-item');
    this.loadedProducts = productNodes.length || this.productGrid.children.length;

    this.updatePaginationInfo();
    this.updateProductsCount();
    this.updateProgressBar();
    this.updateLoadMoreButton();
  }

  updatePaginationInfo() {
    if (!this.paginationInfo) return;

    const showing = Math.min(this.loadedProducts, this.totalProducts || this.loadedProducts);
    this.paginationInfo.innerHTML = `Showing ${showing} of ${this.totalProducts} products`;
  }

  computeRemaining() {
    const remaining = Math.max((this.totalProducts || 0) - this.loadedProducts, 0);
    const remainingToLoad = Math.min(remaining, this.pageSize || 16);
    return { remaining, remainingToLoad };
  }

  updateLoadMoreButton() {
    if (this.paginationContainer) {
      if (this.loadedProducts >= this.totalProducts && this.totalProducts > 0) {
        this.paginationContainer.style.display = 'none';
        return;
      } else {
        this.paginationContainer.style.display = 'block';
      }
    }

    if (!this.loadMoreBtn) return;

    const { remaining, remainingToLoad } = this.computeRemaining();

    if (remaining <= 0 || this.currentPage >= this.totalPages) {
      this.loadMoreBtn.style.display = 'none';
    } else {
      this.loadMoreBtn.style.display = 'inline-block';
      this.loadMoreBtn.disabled = false;
      this.loadMoreBtn.textContent = `Load ${remainingToLoad} More Products`;
      this.loadMoreBtn.setAttribute('aria-live', 'polite');
    }
  }

  updateProductsCount() {
    if (!this.productsCountDisplay) return;

    if (this.loadedProducts >= this.totalProducts && this.totalProducts > 0) {
      this.productsCountDisplay.textContent = 'All products seen';
      this.productsCountDisplay.classList.remove('hidden');
    } else {
      this.productsCountDisplay.textContent = `${this.loadedProducts} products seen`;
      this.productsCountDisplay.classList.remove('hidden');
    }
  }

  updateProgressBar() {
    if (!this.progressContainer && !this.progressBar) return;

    let pct = 0;
    if (this.totalProducts && this.totalProducts > 0) {
      pct = (this.loadedProducts / this.totalProducts) * 100;
    }
    if (pct > 100) pct = 100;
    if (pct < 0) pct = 0;

    const pctStr = Math.round(pct * 100) / 100;

    if (this.progressContainer) {
      this.progressContainer.style.setProperty('--progress-bar', `${pctStr}%`);
      this.progressContainer.setAttribute('aria-valuenow', String(pctStr));
    }

    if (this.progressBar) {
      this.progressBar.style.width = `${pctStr}%`;
      const parent = this.progressBar.closest('[role="progressbar"]');
      if (parent) parent.setAttribute('aria-valuenow', String(pctStr));
    }
  }

  handleFacetUpdate() {
    this.isLoading = false;

    setTimeout(() => {
      this.setupElements();
      this.setupEventListeners();
      this.updateState();
    }, 100);
  }

  getCurrentUrlParams() {
    const urlParams = new URLSearchParams(window.location.search);
    const params = {};

    for (const [key, value] of urlParams.entries()) {
      if (key !== 'page') {
        params[key] = value;
      }
    }

    return params;
  }

  buildNextUrl(page) {
    const params = this.getCurrentUrlParams();
    params.page = page;
    const queryString = new URLSearchParams(params).toString();
    return `${window.location.pathname}?${queryString}`;
  }

  async loadMoreProducts() {
    if (this.isLoading || this.currentPage >= this.totalPages) {
      return;
    }

    this.isLoading = true;

    try {
      const nextPage = this.currentPage + 1;
      const nextUrl = this.buildNextUrl(nextPage);

      const response = await fetch(nextUrl, {
        headers: {
          Accept: 'text/html',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const serverGrid = doc.querySelector('#product-grid');
      if (serverGrid) {
        const dsCurrent = parseInt(serverGrid.dataset.currentPage);
        const dsTotalPages = parseInt(serverGrid.dataset.totalPages);
        const dsProductsCount = parseInt(serverGrid.dataset.productsCount);
        const dsPageSize = parseInt(serverGrid.dataset.pageSize || serverGrid.dataset.paginatePageSize);

        if (!Number.isNaN(dsTotalPages)) this.totalPages = dsTotalPages;
        if (!Number.isNaN(dsProductsCount)) this.totalProducts = dsProductsCount;
        if (!Number.isNaN(dsPageSize)) this.pageSize = dsPageSize;

        if (this.productGrid) {
          if (!Number.isNaN(dsCurrent)) this.productGrid.dataset.currentPage = String(dsCurrent);
          if (!Number.isNaN(dsTotalPages)) this.productGrid.dataset.totalPages = String(dsTotalPages);
          if (!Number.isNaN(dsProductsCount)) this.productGrid.dataset.productsCount = String(dsProductsCount);
          if (!Number.isNaN(dsPageSize)) this.productGrid.dataset.pageSize = String(dsPageSize);
        }
      } else {
        if (this.productGrid) {
          this.productGrid.dataset.currentPage = String(nextPage);
        }
      }

      const newProducts = doc.querySelectorAll('#product-grid .custom-grid-item');

      if (newProducts.length > 0) {
        this.appendProducts(newProducts);
        this.currentPage = nextPage;
        if (this.productGrid) this.productGrid.dataset.currentPage = String(this.currentPage);
        const productNodes = this.productGrid.querySelectorAll('.custom-grid-item');
        this.loadedProducts = productNodes.length || this.loadedProducts + newProducts.length;
        this.updateState();
        this.reinitializeScripts();
      } else {
        this.updateState();
      }
    } catch (error) {
      console.error('InfiniteScroll: Error loading more products', error);
    } finally {
      this.isLoading = false;
    }
  }

  appendProducts(newProducts) {
    const fragment = document.createDocumentFragment();

    newProducts.forEach((product, index) => {
      const cascadeElement = product.querySelector('[data-cascade]');
      if (cascadeElement) {
        const newOrder = this.loadedProducts + index + 1;
        cascadeElement.style.setProperty('--animation-order', newOrder);
      }

      fragment.appendChild(product);
    });

    this.productGrid.appendChild(fragment);
  }

  reinitializeScripts() {
    if (window.theme && typeof window.theme.initializeProductCards === 'function') {
      window.theme.initializeProductCards();
    }

    this.dispatchEvent(
      new CustomEvent('load-more:products-loaded', {
        detail: {
          currentPage: this.currentPage,
          loadedProducts: this.loadedProducts,
          totalProducts: this.totalProducts,
        },
      })
    );
  }

  reset() {
    this.isLoading = false;
    this.updateState();
  }

  getState() {
    return {
      isLoading: this.isLoading,
      currentPage: this.currentPage,
      totalPages: this.totalPages,
      totalProducts: this.totalProducts,
      loadedProducts: this.loadedProducts,
      pageSize: this.pageSize,
    };
  }
}

customElements.define('load-more-products', LoadMoreProducts);
window.LoadMoreProducts = LoadMoreProducts;


if (!customElements.get('grid-layout-switcher')) {
  customElements.define(
    'grid-layout-switcher',
    class GridLayoutSwitcher extends HTMLElement {
      constructor() {
        super();
        this.grid = null;
        this.form = null;
        this.twoColRadio = null;
        this.fourColRadio = null;
      }

      connectedCallback() {
        this.init();
      }

      init() {
        this.form = this.querySelector('form');
        this.grid = document.querySelector('#product-grid');
        this.twoColRadio = document.querySelector('#grid-layout--two');
        this.fourColRadio = document.querySelector('#grid-layout--four');

        if (!this.form || !this.grid) return;

        this.setDefaultLayout();

        this.form.addEventListener('change', this.handleChange.bind(this));

        let resizeTimeout;
        window.addEventListener('resize', () => {
          clearTimeout(resizeTimeout);
          resizeTimeout = setTimeout(() => {
            this.handleResize();
          }, 250);
        });
      }

      setDefaultLayout() {
        const screenWidth = window.innerWidth;

        if (screenWidth > 1024) {
          this.grid.classList.add('custom-grid--4-col-desktop', 'custom-grid--1-col-tablet-down');
          this.grid.classList.remove('custom-grid--2-col-desktop', 'custom-grid--2-col-tablet-down', 'default-desktop');

          if (this.fourColRadio) {
            this.fourColRadio.checked = true;
          }
          if (this.twoColRadio) {
            this.twoColRadio.checked = false;
          }
        } else {
          this.grid.classList.add('custom-grid--2-col-desktop', 'custom-grid--2-col-tablet-down');
          this.grid.classList.remove('custom-grid--4-col-desktop', 'custom-grid--1-col-tablet-down');

          if (this.twoColRadio) {
            this.twoColRadio.checked = true;
          }
          if (this.fourColRadio) {
            this.fourColRadio.checked = false;
          }
        }
      }

      handleChange(e) {
        if (!this.grid) return;

        if (e.target.value.includes('two')) {
          this.applyTwoColumnLayout();
        } else {
          this.applyFourColumnLayout();
        }
      }

      handleResize() {
        console.log('Window resized to:', window.innerWidth);
      }

      applyTwoColumnLayout() {
        this.grid.classList.remove('custom-grid--4-col-desktop', 'custom-grid--1-col-tablet-down');
        this.grid.classList.add('custom-grid--2-col-desktop', 'custom-grid--2-col-tablet-down');
      }

      applyFourColumnLayout() {
        this.grid.classList.remove('custom-grid--2-col-desktop', 'custom-grid--2-col-tablet-down');
        this.grid.classList.add('custom-grid--4-col-desktop', 'custom-grid--1-col-tablet-down');
      }
    }
  );
}

if (!customElements.get('filter-drawer')) {
  customElements.define(
    'filter-drawer',
    class FilterDrawer extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        const body = document.body;

        const openButton = this.querySelector('.mobile-facets__open');
        if (openButton) {
          openButton.addEventListener('click', (e) => {
            this.openFilter();
          });
        }

        document.querySelectorAll('.facet-filters-overlay, .mobile-facets__close').forEach((ele) => {
          ele.addEventListener('click', (e) => {
            const filterDrawer = e.target.closest('filter-drawer');
            if (filterDrawer) {
              filterDrawer.closeFilter();
            }
          });
        });
      }

      openFilter() {
        const openEvent = new CustomEvent('filterOpen', {
          bubbles: true,
          cancelable: true,
          detail: {
            timestamp: Date.now(),
            element: this,
          },
        });

        if (this.dispatchEvent(openEvent)) {
          this.classList.add('active');
          document.body.style.overflow = 'hidden';

          const openedEvent = new CustomEvent('filterOpened', {
            bubbles: true,
            detail: {
              timestamp: Date.now(),
              element: this,
            },
          });
          this.dispatchEvent(openedEvent);
        }
      }

      closeFilter() {
        const closeEvent = new CustomEvent('filterClose', {
          bubbles: true,
          cancelable: true,
          detail: {
            timestamp: Date.now(),
            element: this,
          },
        });

        if (this.dispatchEvent(closeEvent)) {
          this.classList.remove('active');
          document.body.style.overflow = 'auto';

          const closedEvent = new CustomEvent('filterClosed', {
            bubbles: true,
            detail: {
              timestamp: Date.now(),
              element: this,
            },
          });
          this.dispatchEvent(closedEvent);
        }
      }

      open() {
        this.openFilter();
      }

      close() {
        this.closeFilter();
      }

      toggle() {
        if (this.classList.contains('active')) {
          this.closeFilter();
        } else {
          this.openFilter();
        }
      }

      isOpen() {
        return this.classList.contains('active');
      }
    }
  );
}
